import React from 'react'
import { useForm } from 'react-hook-form'
import Trans from 'next-translate/Trans'
import {
  CreateUserInput,
  LocaleEnum,
  useCreateUserMutation,
} from 'generated/apolloComponents'
import useTranslation from 'next-translate/useTranslation'
import { signIn } from 'next-auth/react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import Link from 'next/link'
import { EMAIL_FORMAT } from 'libs/constants/forms'
import Script from 'next/script'
import { refererCookie } from 'libs/cookies'
import FormInput from '../atoms/Form/FormInput'
import CheckboxInput from '../atoms/Form/CheckboxInput'
import { CaptchaResult, handleReCaptchaVerify } from '@/helpers/utils'

type Variables = {
  input: CreateUserInput
}
interface IProps {
  callbackUrl: string
}

type FormData = {
  email: string
  password: string
  phone: string
  companyName: string
  name: string
  terms: boolean
  newsletter: boolean
}

const RegisterUserForm = ({ callbackUrl }: IProps) => {
  const { t, lang } = useTranslation('common')

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t('validation_messages:required'))
      .matches(EMAIL_FORMAT, t('validation_messages:email_invalid_format')),
    password: Yup.string()
      .required(t('validation_messages:required'))
      .min(8, t('validation_messages:password_invalid_format')),
    phone: Yup.string().required(t('validation_messages:required')),
    name: Yup.string().required(t('validation_messages:required')),
    companyName: Yup.string().required(t('validation_messages:required')),
    terms: Yup.boolean()
      .required(t('validation_messages:terms_must_accept'))
      .oneOf([true], t('validation_messages:terms_must_accept')),
  })

  const formOptions = { resolver: yupResolver(validationSchema) }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm(formOptions)

  const [createUserMutation] = useCreateUserMutation()

  const registerUser = async (formData: FormData) => {
    const { email, password, name, companyName, phone, terms, newsletter } =
      formData
    const variables: Variables = {
      input: {
        credentials: {
          email,
          password,
          name,
          companyName,
          phone,
          terms,
          newsletter,
        },
        referer: refererCookie(),
        language: lang as LocaleEnum,
      },
    }

    const { data: createUser } = await createUserMutation({ variables })
    if (createUser && createUser.createUser?.success === true) {
      signIn('user_email_password', {
        email,
        password,
        callbackUrl: callbackUrl,
      })
    } else {
      setError('email', { message: t('validation_messages:invalid_email') })
    }
  }

  const onSubmit = async (data: FormData) => {
    const result: CaptchaResult = await handleReCaptchaVerify()
    if (result.success === true) registerUser(data)
  }

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
      />
      <form
        onSubmit={handleSubmit(
          async (data) => await onSubmit(data as FormData)
        )}
      >
        <FormInput
          errors={errors}
          options={{ ...register('companyName', { required: true }) }}
          label={t('common:company_name')}
          type='text'
        />
        <FormInput
          errors={errors}
          options={{ ...register('name', { required: true }) }}
          label={t('common:user_name')}
          type='text'
        />
        <FormInput
          errors={errors}
          options={{ ...register('email', { required: true }) }}
          label={t('common:user_email')}
          type='email'
        />
        <FormInput
          errors={errors}
          options={{ ...register('phone', { required: true }) }}
          label={t('common:phone')}
          type='tel'
        />
        <FormInput
          errors={errors}
          options={{ ...register('password', { required: true }) }}
          label={
            <>
              {t('common:password')}{' '}
              <small style={{ fontWeight: 100 }}>
                {t('common:password_hint')}
              </small>
            </>
          }
          type='password'
        />
        <p className='text-xxs font-light'>{t('common:policy')}</p>
        <CheckboxInput
          id='terms'
          errors={errors}
          options={{ ...register('terms', { required: true }) }}
          label={
            <Trans
              i18nKey='common:user_terms_html'
              components={[
                <p />,
                <a href='/terms' target='_blank' className='text-action' />,
                <a
                  href='/privacy_policy'
                  target='_blank'
                  className='text-action'
                />,
              ]}
            />
          }
          type='checkbox'
        />
        <CheckboxInput
          id='newsletter'
          errors={errors}
          options={{ ...register('newsletter', { required: false }) }}
          label={t('common:newsletter')}
          type='checkbox'
        />
        <input
          type='submit'
          value={t('common:user_signup_button')}
          className='mt-6 primary w-full focus:outline-none transition ease-in-out duration-300 cursor-pointer rounded-full  whitespace-nowrap relative inline-block py-2 px-9 text-base bg-primary hover:bg-primary-hover text-white border-3 border-primary hover:border-primary-hover font-normal'
        />
      </form>
      <div className='flex justify-center mt-8'>
        <Link href='/auth/sign_in' prefetch={false} legacyBehavior>
          <div>
            <span className='font-normal'>
              {t('common:account_already_registered')}
            </span>{' '}
            <a className='cursor-pointer font-medium text-sm underline hover:no-underline'>
              {t('common:login')}
            </a>
          </div>
        </Link>
      </div>
    </>
  )
}

export default RegisterUserForm
